import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Banner } from "@web-monorepo/mds/components/banner";
import { useLocation } from "@reach/router";
import cookies from "cookies-js";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { AppDataContext } from "@src/components/AppDataContext";
import { cookiesWork } from "@src/utils/cookies";
import { logEvent } from "@src/utils/logClient";

const COOKIE_NAME = "school_leaders_bts_2026";
const BANNER_HREF = "/school-leaders/?utm_source=hellobar";
const TRACKING_EVENT = "web.external_page.bts_2026.hello_bar.tap";

interface DistrictsHelloBarProps {
  zIndex?: number;
}

const createCookieState = (cookieName: string) => ({
  isSet: (): boolean => (cookiesWork() ? cookies.get(cookieName) === "true" : true),
  set: () => cookiesWork() && cookies.set(cookieName, "true", { expires: 365 }),
});

const DistrictsHelloBar = ({ zIndex }: DistrictsHelloBarProps) => {
  const location = useLocation();
  const { translationLocaleAsString } = useContext(TranslationContext);
  const {
    data: { loadingSession, type, userData },
  } = useContext(AppDataContext);
  const lang = (translationLocaleAsString?.() ?? "en-us").toLowerCase();
  const isUsEnglish = lang === "en" || lang === "en-us";
  const cookieState = useMemo(() => createCookieState(COOKIE_NAME), []);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!cookieState.isSet()) setIsOpen(true);
  }, [cookieState]);

  const handleClick = useCallback(() => {
    cookieState.set();
    setIsOpen(false);
    logEvent({
      eventName: TRACKING_EVENT,
      eventValue: window.location.href,
    });
  }, [cookieState]);

  // Only show on main index page
  if (location.pathname !== "/") return null;
  if (!isUsEnglish) return null;
  if (loadingSession) return null;

  const isLoggedOut = !userData;
  const isSchoolLeader = type === "teacher" && userData?.isSchoolAdmin === true;
  if (!isLoggedOut && !isSchoolLeader) return null;

  return (
    <Banner
      variant="default"
      href={BANNER_HREF}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      onClick={handleClick}
      style={zIndex != null ? { zIndex } : undefined}
    >
      New for school leaders this back to school. Check it out
    </Banner>
  );
};

export default DistrictsHelloBar;
